import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Boxes, LogOut, Settings, LayoutDashboard, Network, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";

const ROLE_LABEL = { admin: "ADMIN", annezin: "ANNEZIN / DISTRIBUTION", ddo: "DDO", ferme_production: "FERME PRODUCTION" };

export default function AppShell({ children }) {
  const { user, logout, can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const canLogistics = can && can("logistics", "view");
  const isDDO = user?.role === "ddo" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const canManage =
    user?.role === "admin" ||
    ["users", "inventory", "initial_stock", "types", "sites", "audit"].some((m) => {
      if (m === "users" && user?.role !== "annezin") return false;
      const cell = (user?.permissions || {})[m] || {};
      return !!(cell.admin || cell.view || cell.edit);
    });

  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="h-16 sticky top-0 z-50 bg-slate-900 text-white flex items-center justify-between px-3 sm:px-5 shadow-md">
        <button
          onClick={() => nav("/")}
          className="flex items-center gap-2 min-w-0"
          data-testid="header-home-button"
          aria-label="Accueil — tableau de bord"
        >
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
            <Boxes className="h-5 w-5" />
          </div>
          <div className="text-left min-w-0 hidden xs:block">
            <div className="font-display font-extrabold text-sm sm:text-base leading-tight truncate uppercase tracking-tight">
              Suivi Emballages
            </div>
          </div>
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          <span className="bg-blue-600 text-white text-xs font-data font-bold px-3 py-1.5 rounded-full uppercase tracking-wider" data-testid="role-badge">
            {user?.role === "ferme_production" ? (user?.site_name || user?.site || ROLE_LABEL.ferme_production) : (ROLE_LABEL[user?.role] || user?.role)}
          </span>
          <span className="hidden sm:inline text-sm text-slate-300 truncate max-w-[140px]">{user?.name}</span>
          {isDDO && (
            <Button
              variant="ghost"
              size="icon"
              className={`text-white hover:bg-slate-700 h-10 w-10 ${loc.pathname.startsWith("/ddo") ? "bg-slate-700" : ""}`}
              onClick={() => nav(loc.pathname.startsWith("/ddo") ? "/" : "/ddo")}
              data-testid="nav-ddo-button"
              title="Espace DDO"
              aria-label="Espace DDO"
            >
              <Warehouse className="h-5 w-5" />
            </Button>
          )}
          {canLogistics && (
            <Button
              variant="ghost"
              size="icon"
              className={`text-white hover:bg-slate-700 h-10 w-10 ${loc.pathname.startsWith("/logistics") ? "bg-slate-700" : ""}`}
              onClick={() => nav(loc.pathname.startsWith("/logistics") ? "/" : "/logistics")}
              data-testid="nav-logistics-button"
              title="Pilotage logistique"
              aria-label="Pilotage logistique"
            >
              <Network className="h-5 w-5" />
            </Button>
          )}
          {canManage && (
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-slate-700 h-10 w-10"
              onClick={() => nav(loc.pathname.startsWith("/admin") ? "/" : "/admin")}
              data-testid="nav-admin-button"
              title={loc.pathname.startsWith("/admin") ? "Tableau de bord" : "Administration"}
              aria-label={loc.pathname.startsWith("/admin") ? "Tableau de bord" : "Administration"}
            >
              {loc.pathname.startsWith("/admin") ? <LayoutDashboard className="h-5 w-5" /> : <Settings className="h-5 w-5" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-slate-700 h-10 w-10"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            data-testid="logout-button"
            title="Déconnexion"
            aria-label="Déconnexion"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-5 pt-4 sm:pt-6">{children}</main>
    </div>
  );
}
